import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import NavigationBar from "components/Navigation";
import { facilityService } from "services/facilityService";
import {
  SearchContainer,
  Header,
  BackButton,
  HeaderTitle,
  SearchSection,
  SearchInputBox,
  SearchInput,
  SearchIcon,
  SearchContent,
  SectionTitle,
  RecentSearches,
  SearchTag,
  TagText,
  DeleteButton,
  PopularList,
  PopularItem,
  ItemNumber,
  ItemText
} from "./Search.styles";

const GUEST_SEARCH_KEY = "guestRecentSearches";

const Search: React.FC = () => {
  const isGuest = localStorage.getItem("guestMode") === "true";
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [popularSearches, setPopularSearches] = useState<string[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (isGuest) {
      const saved = localStorage.getItem(GUEST_SEARCH_KEY);
      setRecentSearches(saved ? JSON.parse(saved) : []);
    } else {
      const fetchSearchHistory = async () => {
        try {
          const history = await facilityService.getSearchHistory();
          setRecentSearches(history.map(item => item.keyword));
        } catch (error) {
          console.error("Failed to fetch search history:", error);
        }
      };
      fetchSearchHistory();
    }

    const fetchPopularSearches = async () => {
      try {
        const popular = await facilityService.getPopularSearches();
        setPopularSearches(popular.map(item => item.keyword));
      } catch (error) {
        console.error("Failed to fetch popular searches:", error);
      }
    };
    fetchPopularSearches();
  }, [isGuest]);

  const handleSearchInputClick = () => {
    navigate("/search-results");
  };

  const handleRecentSearchClick = async (query: string) => {
    if (!isGuest) {
      try {
        await facilityService.saveSearchLog(query);
      } catch (error) {
        console.error("Failed to save search log:", error);
      }
    }
    navigate(`/search-results?q=${encodeURIComponent(query)}`);
  };

  const handleDeleteRecentSearch = async (query: string) => {
    if (isGuest) {
      const saved = localStorage.getItem(GUEST_SEARCH_KEY);
      const history: string[] = saved ? JSON.parse(saved) : [];
      const updated = history.filter(s => s !== query);
      localStorage.setItem(GUEST_SEARCH_KEY, JSON.stringify(updated));
      setRecentSearches(updated);
    } else {
      try {
        await facilityService.deleteSearchHistory(query);
        setRecentSearches(prev => prev.filter(s => s !== query));
      } catch (error) {
        console.error("Failed to delete search history:", error);
      }
    }
  };

  const handlePopularSearchClick = async (query: string) => {
    if (!isGuest) {
      try {
        await facilityService.saveSearchLog(query);
      } catch (error) {
        console.error("Failed to save search log:", error);
      }
    }
    navigate(`/search-results?q=${encodeURIComponent(query)}`);
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <>
      <SearchContainer>
        <Header>
          <BackButton onClick={handleBack}>
            <FontAwesomeIcon icon={faChevronLeft} />
          </BackButton>
          <HeaderTitle>검색</HeaderTitle>
        </Header>
        <SearchSection>
          <SearchInputBox onClick={handleSearchInputClick}>
            <SearchInput
              type="text"
              placeholder="검색어를 입력하세요"
              readOnly
            />
            <SearchIcon>
              <FontAwesomeIcon icon={faMagnifyingGlass} />
            </SearchIcon>
          </SearchInputBox>
        </SearchSection>

        <SearchContent>
          {recentSearches.length > 0 && (
            <>
              <SectionTitle>최근 검색어</SectionTitle>
              <RecentSearches>
                {recentSearches.map((search, index) => (
                  <SearchTag key={index}>
                    <TagText onClick={() => handleRecentSearchClick(search)}>
                      {search}
                    </TagText>
                    <DeleteButton onClick={() => handleDeleteRecentSearch(search)}>
                      ✕
                    </DeleteButton>
                  </SearchTag>
                ))}
              </RecentSearches>
            </>
          )}

          {popularSearches.length > 0 && (
            <>
              <SectionTitle>인기 검색어</SectionTitle>
              <PopularList>
                {popularSearches.map((search, index) => (
                  <PopularItem key={index} onClick={() => handlePopularSearchClick(search)}>
                    <ItemNumber>{index + 1}.</ItemNumber>
                    <ItemText>{search}</ItemText>
                  </PopularItem>
                ))}
              </PopularList>
            </>
          )}
        </SearchContent>
      </SearchContainer>
      <NavigationBar />
    </>
  );
};

export default Search;
